<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import ContactAPI from 'dashboard/api/contacts';
import { useLeadTier, resolveLeadTier } from 'dashboard/composables/useLeadTier';

// Painel "Score de Lead" no ContactInfoPanel (aba/accordion ao lado das
// existentes). Mostra:
//  - Score atual + perfil HOT/WARM/COLD + data do snapshot
//  - Critérios (breakdown do JSON salvo em lead_scores.analysis)
//  - Sugestões do modelo
//  - Histórico últimos 5 scores
//
// Dados vêm de /api/v1/accounts/:account/contacts/:id/lead_scores.
// Estado vazio quando contato nunca foi analisado — mostra mensagem discreta
// explicando que o cron noturno vai processar na próxima janela.
const props = defineProps({
  contactId: { type: [Number, String], required: true },
  contact: { type: Object, default: () => ({}) },
});

const loading = ref(false);
const error = ref(null);
const scores = ref([]);

const currentScore = computed(() => {
  return scores.value.find(s => s.status === 'ready') || null;
});

const latestAny = computed(() => {
  return scores.value[0] || null;
});

const hasHistory = computed(() => scores.value.length > 0);

// Régua única de tier — perfil salvo pela IA tem prioridade sobre o score
const {
  tier: leadTier,
  label: leadTierLabel,
  colorClass: leadTierColor,
  icon: leadTierIcon,
} = useLeadTier(
  () => currentScore.value?.score,
  () => currentScore.value?.lead_profile
);

const scoreTier = computed(() => {
  if (!leadTier.value) return null;
  return {
    label: leadTierLabel.value,
    icon: leadTierIcon.value,
    klass: leadTierColor.value,
  };
});

const progressWidth = computed(() => {
  const s = currentScore.value?.score || 0;
  return `${Math.min(100, Math.max(0, s))}%`;
});

const criteriaEntries = computed(() => {
  const analysis = currentScore.value?.analysis || {};
  if (!analysis || typeof analysis !== 'object') return [];
  return Object.entries(analysis).map(([name, data]) => ({
    name,
    score: typeof data === 'object' ? (data.score ?? null) : null,
    evidence: typeof data === 'object' ? (data.evidence ?? null) : null,
    notes: typeof data === 'object' ? (data.notes ?? null) : null,
  }));
});

const historyEntries = computed(() => {
  return scores.value.filter(s => s.status === 'ready').slice(0, 5);
});

function profileIcon(profile) {
  return resolveLeadTier(null, profile)?.icon || 'i-ri-subtract-line';
}

function formatDate(iso) {
  if (!iso) return '';
  // pt-BR 24h sempre (hour12 explicito evita AM/PM em browsers com locale en-US)
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

async function load() {
  if (!props.contactId) return;
  loading.value = true;
  error.value = null;
  try {
    const { data } = await ContactAPI.getLeadScores(props.contactId, { limit: 10 });
    scores.value = data?.data || [];
  } catch (e) {
    error.value = e?.response?.data?.message || 'Erro ao carregar scores';
  } finally {
    loading.value = false;
  }
}

onMounted(load);

watch(() => props.contactId, () => { load(); });
</script>

<template>
  <div class="py-2 space-y-3">
    <div v-if="loading" class="text-xs text-n-slate-11">Carregando...</div>
    <div v-else-if="error" class="text-xs text-n-ruby-11">{{ error }}</div>

    <div
      v-else-if="!hasHistory"
      class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-xl px-3 py-3"
    >
      <div class="flex items-center gap-2">
        <span class="i-ri-flashlight-line size-4 text-n-slate-10" />
        <span>Aguardando análise — o scoring noturno processa leads com atividade recente.</span>
      </div>
      <p class="mt-2 text-[11px] text-n-slate-11">
        Memberships ativas em WhatsApp Groups entram no prompt via groups_context
        (level/score/launch), mesmo quando conversas de grupo são ignoradas.
      </p>
    </div>

    <template v-else>
      <!-- Score atual -->
      <div v-if="currentScore" class="space-y-2">
        <div class="flex items-center gap-3">
          <div class="flex-1">
            <div class="flex items-baseline gap-2">
              <span class="text-2xl font-extrabold text-n-slate-12">{{ currentScore.score }}</span>
              <span class="text-xs text-n-slate-11">/ 100</span>
              <span
                v-if="scoreTier"
                class="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                :class="scoreTier.klass"
              >
                <span :class="[scoreTier.icon, 'size-3.5']" />
                {{ scoreTier.label }}
              </span>
            </div>
            <div class="w-full bg-n-alpha-2 rounded-full h-2 mt-1 overflow-hidden">
              <div
                class="h-2 rounded-full"
                :class="scoreTier ? scoreTier.klass : 'bg-n-alpha-3'"
                :style="{ width: progressWidth }"
              />
            </div>
          </div>
        </div>
        <p class="text-[11px] text-n-slate-11">
          Atualizado em {{ formatDate(currentScore.scored_at) }}
        </p>
      </div>

      <!-- Critérios -->
      <div v-if="criteriaEntries.length" class="space-y-1.5 border-t border-n-weak pt-2">
        <h4 class="text-xs font-semibold text-n-slate-12">Critérios</h4>
        <div
          v-for="c in criteriaEntries"
          :key="c.name"
          class="text-xs space-y-0.5"
        >
          <div class="flex items-center gap-2">
            <span class="text-n-slate-12 capitalize flex-1 truncate">{{ c.name }}</span>
            <span v-if="c.score !== null" class="font-mono text-n-slate-11">{{ c.score }}</span>
          </div>
          <p v-if="c.notes" class="text-[11px] text-n-slate-10 leading-snug">{{ c.notes }}</p>
        </div>
      </div>

      <!-- Sugestões -->
      <div
        v-if="currentScore && currentScore.suggestions"
        class="border-t border-n-weak pt-2"
      >
        <h4 class="text-xs font-semibold text-n-slate-12 flex items-center gap-1">
          <span class="i-ri-lightbulb-line size-3.5" />
          Sugestões
        </h4>
        <p class="text-[11px] text-n-slate-11 leading-relaxed mt-1 whitespace-pre-line">
          {{ currentScore.suggestions }}
        </p>
      </div>

      <!-- Histórico -->
      <div
        v-if="historyEntries.length > 1"
        class="border-t border-n-weak pt-2"
      >
        <h4 class="text-xs font-semibold text-n-slate-12 mb-1">Histórico (últimos 5)</h4>
        <ul class="space-y-1">
          <li
            v-for="h in historyEntries.slice(0, 5)"
            :key="h.id"
            class="flex items-center gap-2 text-[11px] text-n-slate-11"
          >
            <span class="font-mono w-8 text-right">{{ h.score }}</span>
            <span>{{ formatDate(h.scored_at) }}</span>
            <span :class="[profileIcon(h.lead_profile), 'size-3 ml-auto']" />
          </li>
        </ul>
      </div>

      <!-- Apenas snapshots falhados/pulados -->
      <div
        v-if="!currentScore && latestAny"
        class="text-[11px] text-n-slate-11 bg-n-alpha-1 rounded-xl px-3 py-2"
      >
        Último processamento em {{ formatDate(latestAny.created_at) }} — status <strong>{{ latestAny.status }}</strong>.
        <span v-if="latestAny.error"> ({{ latestAny.error }})</span>
      </div>
    </template>
  </div>
</template>
